import { supabase } from "../../lib/supabase";

/*
  NEWSLETTER SERVICE
  
  Table used: newsletter_subscribers
*/

/* Subscribe to newsletter */
export async function subscribeToNewsletter(email) {
  try {
    const { data, error } = await supabase
      .from("newsletter_subscribers")
      .insert([{ email }])
      .select();

    if (error) {
      // Check if it's a duplicate email error
      if (error.code === "23505") {
        return { success: false, message: "This email is already subscribed!" };
      }
      console.error("Newsletter subscription error:", error);
      return { success: false, message: "Failed to subscribe. Please try again." };
    }

    return { success: true, message: "Successfully subscribed to newsletter!", data };
  } catch (error) {
    console.error("Newsletter subscription error:", error);
    return { success: false, message: "An unexpected error occurred." };
  }
}

/* Get all newsletter subscribers */
export async function getAllSubscribers() {
  try {
    const { data, error } = await supabase
      .from("newsletter_subscribers")
      .select("*")
      .order("subscribed_at", { ascending: false });

    if (error) {
      console.error("Get subscribers error:", error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error("Get subscribers error:", error);
    return [];
  }
}

/* Get active subscribers count */
export async function getActiveSubscribersCount() {
  try {
    const { count, error } = await supabase
      .from("newsletter_subscribers")
      .select("*", { count: "exact", head: true })
      .eq("is_active", true);

    if (error) {
      console.error("Active subscribers count error:", error);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error("Active subscribers count error:", error);
    return 0;
  }
}

/* Unsubscribe from newsletter */
export async function unsubscribeFromNewsletter(id) {
  try {
    const { error } = await supabase
      .from("newsletter_subscribers")
      .update({
        is_active: false,
        unsubscribed_at: new Date().toISOString(),
      })
      .eq("id", id);

    if (error) {
      console.error("Unsubscribe error:", error);
      return { success: false, message: "Failed to unsubscribe" };
    }

    return { success: true, message: "Successfully unsubscribed" };
  } catch (error) {
    console.error("Unsubscribe error:", error);
    return { success: false, message: "An unexpected error occurred" };
  }
}

/* Reactivate subscriber */
export async function reactivateSubscriber(id) {
  try {
    const { error } = await supabase
      .from("newsletter_subscribers")
      .update({
        is_active: true,
        unsubscribed_at: null,
      })
      .eq("id", id);

    if (error) {
      console.error("Reactivate subscriber error:", error);
      return { success: false, message: "Failed to reactivate subscriber" };
    }

    return { success: true, message: "Successfully reactivated subscriber" };
  } catch (error) {
    console.error("Reactivate subscriber error:", error);
    return { success: false, message: "An unexpected error occurred" };
  }
}

/* Delete subscriber */
export async function deleteSubscriber(id) {
  try {
    const { error } = await supabase
      .from("newsletter_subscribers")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Delete subscriber error:", error);
      return { success: false, message: "Failed to delete subscriber" };
    }

    return { success: true, message: "Successfully deleted subscriber" };
  } catch (error) {
    console.error("Delete subscriber error:", error);
    return { success: false, message: "An unexpected error occurred" };
  }
}

/* Get newsletter daily stats */
export async function getNewsletterDaily() {
  try {
    const { data, error } = await supabase.rpc("newsletter_daily_stats");

    if (error) {
      console.error("Newsletter daily stats error:", error);
      return [];
    }

    return (data || []).map((row) => ({
      label: row.day,
      value: Number(row.count),
    }));
  } catch (error) {
    console.error("Newsletter daily stats error:", error);
    return [];
  }
}

/* Get newsletter monthly stats */
export async function getNewsletterMonthly() {
  try {
    const { data, error } = await supabase.rpc("newsletter_monthly_stats");

    if (error) {
      console.error("Newsletter monthly stats error:", error);
      return [];
    }

    return (data || []).map((row) => ({
      label: row.month,
      value: Number(row.count),
    }));
  } catch (error) {
    console.error("Newsletter monthly stats error:", error);
    return [];
  }
}

/* Add subscriber manually (admin) */
export async function addSubscriberManually(email) {
  try {
    const { data, error } = await supabase
      .from("newsletter_subscribers")
      .insert([{ email, is_active: true }])
      .select();

    if (error) {
      if (error.code === "23505") {
        return { success: false, message: "This email is already in the system!" };
      }
      console.error("Add subscriber error:", error);
      return { success: false, message: "Failed to add subscriber" };
    }

    return { success: true, message: "Subscriber added successfully!", data };
  } catch (error) {
    console.error("Add subscriber error:", error);
    return { success: false, message: "An unexpected error occurred" };
  }
}
